import { ReactNode, useEffect, useRef } from "react";
import { LuAlertTriangle, LuLoader2 } from "react-icons/lu";

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "danger" | "primary";
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/** Modal "Are you sure?" step for destructive or important actions */
const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "danger",
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) => {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    cancelRef.current?.focus(); // safe default: focus Cancel, not the destructive button
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, loading, onCancel]);

  if (!open) return null;

  const danger = tone === "danger";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div aria-hidden className="absolute inset-0 bg-black/40" onClick={() => !loading && onCancel()} />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby={message ? "confirm-message" : undefined}
        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-pop"
      >
        <div className="flex gap-4">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
              danger ? "bg-red-50 text-red-600" : "bg-hc_F4EBFA text-honeConnectPurple"
            }`}
          >
            <LuAlertTriangle className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <h2 id="confirm-title" className="text-base font-semibold text-honeConnectBlack">
              {title}
            </h2>
            {message && (
              <div id="confirm-message" className="mt-1.5 text-sm leading-relaxed text-honeConnectGray">
                {message}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="h-10 rounded-lg border border-hcBorder bg-white px-4 text-sm font-medium text-honeConnectBlack transition-colors hover:bg-hcBg disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-honeConnectPurple/40"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold text-white transition-colors disabled:opacity-70 focus:outline-none focus-visible:ring-2 ${
              danger
                ? "bg-red-600 hover:bg-red-700 focus-visible:ring-red-500/40"
                : "bg-honeConnectPurple hover:bg-hc_59077A focus-visible:ring-honeConnectPurple/40"
            }`}
          >
            {loading && <LuLoader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
